import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Button,
  Badge,
  EmptyState,
  Text,
  Banner,
  CalloutCard,
  Modal,
  BlockStack,
  InlineStack,
  Spinner,
} from '@shopify/polaris';
import {
  PlusIcon,
  EditIcon,
  DeleteIcon,
  DuplicateIcon,
  ViewIcon,
  HideIcon,
} from '@shopify/polaris-icons';

import { useStore } from '../providers/StoreProvider';
import AppLayout from '../components/AppLayout';
import BarPreview from '../components/BarPreview';

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$',
  CHF: 'CHF', CNY: '¥', INR: '₹', BRL: 'R$', MXN: 'MX$', KRW: '₩',
  SGD: 'S$', HKD: 'HK$', SEK: 'kr', NOK: 'kr', DKK: 'kr', NZD: 'NZ$',
  ZAR: 'R', AED: 'AED', SAR: 'SAR', THB: '฿', MYR: 'RM', IDR: 'Rp',
  PHP: '₱', TWD: 'NT$', PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
};

function getCurrencySymbol(code) {
  return (code && CURRENCY_SYMBOLS[code]) || code || '$';
}

const DISMISSED_KEY = (shop) => `jambofy_embed_setup_dismissed_${shop}`;

/**
 * Check if the embed setup prompt should be shown.
 * Returns true (dismissed) only if the stored dismissal timestamp is
 * newer than the store's last install/reinstall timestamp.
 */
function isDismissedAfterInstall(shop, lastInstalledAt) {
  if (!shop) return false;
  const raw = localStorage.getItem(DISMISSED_KEY(shop));
  if (!raw) return false;
  // lastInstalledAt is explicitly null (migration ran but column is NULL) -
  // honour any stored dismissal for backwards compat.
  // undefined means stale cache - don't treat as dismissed.
  if (lastInstalledAt === null) return true;
  if (lastInstalledAt === undefined) return false;
  const dismissedAt = new Date(raw).getTime();
  // Legacy '1' values can't be compared to a date - treat as stale so the
  // prompt re-shows once (user gets a proper ISO timestamp on next dismiss)
  if (isNaN(dismissedAt)) return false;
  return dismissedAt >= new Date(lastInstalledAt).getTime();
}

function Dashboard() {
  const navigate = useNavigate();
  const {
    store,
    bars,
    isPremium,
    isInTrial,
    billing,
    canCreateBar,
    toggleBar,
    deleteBar,
    duplicateBar,
    fetchBars,
    isLoading,
  } = useStore();

  // Re-fetch the bars list on every dashboard mount so navigation back from
  // BarEditor / Settings / Billing always shows current data (and refreshes
  // the sessionStorage cache via fetchBars). The initial bootstrap in
  // StoreProvider handles the first load, so skip while still loading to
  // avoid a redundant fetch.
  useEffect(() => {
    if (!isLoading) fetchBars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shop = store?.shop_domain;
  const lastInstalledAt = store?.last_installed_at;
  const storeCurrency = store?.shop_currency || 'USD';

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [barToDelete, setBarToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [setupDismissed, setSetupDismissed] = useState(() =>
    isDismissedAfterInstall(shop, lastInstalledAt)
  );

  // Re-evaluate when store data loads (shop / last_installed_at become available)
  useEffect(() => {
    if (shop) {
      setSetupDismissed(isDismissedAfterInstall(shop, lastInstalledAt));
    }
  }, [shop, lastInstalledAt]);

  const dismissSetup = useCallback(() => {
    if (shop) localStorage.setItem(DISMISSED_KEY(shop), new Date().toISOString());
    setSetupDismissed(true);
  }, [shop]);

  const handleOpenThemeEditor = useCallback(() => {
    if (store?.shop_domain) {
      window.open(
        `https://${store.shop_domain}/admin/themes/current/editor?context=apps`,
        '_blank'
      );
    }
    dismissSetup();
  }, [store?.shop_domain, dismissSetup]);

  const handleToggle = useCallback(async (barId) => {
    setActionLoading(barId);
    const result = await toggleBar(barId);
    setActionLoading(null);

    if (!result.success && result.error?.upgrade_required) {
      // Show upgrade prompt
      navigate('/app/billing');
    }
  }, [toggleBar, navigate]);

  const handleDelete = useCallback(async () => {
    if (!barToDelete) return;

    setActionLoading(barToDelete);
    await deleteBar(barToDelete);
    setActionLoading(null);
    setDeleteModalOpen(false);
    setBarToDelete(null);
  }, [barToDelete, deleteBar]);

  const handleDuplicate = useCallback(async (barId) => {
    setActionLoading(barId);
    const result = await duplicateBar(barId);
    setActionLoading(null);

    if (!result.success && result.error?.upgrade_required) {
      navigate('/app/billing');
    }
  }, [duplicateBar, navigate]);

  const confirmDelete = useCallback((barId) => {
    setBarToDelete(barId);
    setDeleteModalOpen(true);
  }, []);

  const getBarDisplayInfo = useCallback((bar) => {
    const { free_shipping_threshold, target_currency, use_exchange_rate, custom_exchange_rate } = bar;
    const storeCurrencySymbol = getCurrencySymbol(storeCurrency);
    const hasConversion = target_currency && target_currency !== storeCurrency;
    const targetSymbol = hasConversion ? getCurrencySymbol(target_currency) : null;

    const customConverted = hasConversion && !use_exchange_rate && custom_exchange_rate
      ? free_shipping_threshold * custom_exchange_rate
      : null;

    const displayThreshold = customConverted != null ? customConverted : free_shipping_threshold;
    const displaySymbol = hasConversion ? targetSymbol : storeCurrencySymbol;

    let thresholdLabel;
    if (hasConversion) {
      const base = `${storeCurrencySymbol}${free_shipping_threshold} ${storeCurrency}`;
      const converted = customConverted != null
        ? `${targetSymbol}${customConverted.toFixed(2)} ${target_currency}`
        : `${target_currency} (live rate)`;
      thresholdLabel = `${base} → ${converted}`;
    } else {
      thresholdLabel = `${storeCurrencySymbol}${free_shipping_threshold}`;
    }

    return { displayThreshold, displaySymbol, thresholdLabel };
  }, [storeCurrency]);

  return (
    <AppLayout>
      <Page
        title="Free Shipping Bars"
        primaryAction={{
          content: 'Create Bar',
          icon: PlusIcon,
          onAction: () => navigate('/app/bars/new'),
          disabled: !canCreateBar,
        }}
      >
        <Layout>
          {/* One-time embed setup prompt */}
          {!setupDismissed && (
            <Layout.Section>
              <CalloutCard
                title="Enable your shipping bar in the theme"
                illustration="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                primaryAction={{
                  content: 'Open Theme Editor',
                  onAction: handleOpenThemeEditor,
                }}
                onDismiss={dismissSetup}
              >
                <p>
                  Your bar is ready, but needs to be turned on in the Shopify Theme Editor.
                  Go to <strong>App embeds</strong> and toggle on{' '}
                  <strong>Jambofy Free Shipping Bar</strong>.
                </p>
              </CalloutCard>
            </Layout.Section>
          )}

          {/* Trial Banner */}
          {isInTrial && (
            <Layout.Section>
              <Banner
                title="Premium Trial Active"
                tone="info"
                action={{
                  content: 'View Plan Details',
                  onAction: () => navigate('/app/billing'),
                }}
              >
                <p>
                  Your premium access ends on{' '}
                  {billing?.trial_ends_at
                    ? new Date(billing.trial_ends_at).toLocaleDateString()
                    : 'soon'
                  }
                  {billing?.trial_days_remaining != null &&
                    ` (${billing.trial_days_remaining} day${billing.trial_days_remaining !== 1 ? 's' : ''} remaining)`
                  }.
                  Enjoy all premium features!
                </p>
              </Banner>
            </Layout.Section>
          )}

          {/* Upgrade Banner for Basic */}
          {!isPremium && !isInTrial && bars.length > 0 && (
            <Layout.Section>
              <Banner
                title="Upgrade to Premium"
                tone="warning"
                action={{
                  content: billing?.trial_used
                    ? 'Upgrade to Premium'
                    : 'Start Free Trial',
                  onAction: () => navigate('/app/billing'),
                }}
              >
                <p>
                  Get unlimited bars, tiered rewards, cart drawer placement,
                  geo targeting, auto currency conversion, background images,
                  scheduling, and analytics.
                </p>
              </Banner>
            </Layout.Section>
          )}


          {/* Bars List */}
          <Layout.Section>
            {bars.length === 0 ? (
              <Card padding="0">
                <EmptyState
                  heading="Create your first shipping bar"
                  action={{
                    content: 'Create Bar',
                    onAction: () => navigate('/app/bars/new'),
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Encourage customers to add more items to their cart
                    with a customizable free shipping progress bar.
                  </p>
                </EmptyState>
              </Card>
            ) : (
              <BlockStack gap="500">
                {bars.map((bar) => {
                  const { id, name, is_active, position } = bar;
                  const activeCount = bars.filter(b => b.is_active).length;
                  const isLockedBar = !isPremium && !is_active && activeCount >= 1;
                  const { displayThreshold, displaySymbol, thresholdLabel } = getBarDisplayInfo(bar);
                  const positionLabel = position === 'top_sticky' ? 'Top (Sticky)'
                    : position === 'bottom_sticky' ? 'Bottom (Sticky)'
                    : position;

                  return (
                    <div
                      key={id}
                      style={{
                        ...(isLockedBar ? { opacity: 0.45 } : { cursor: 'pointer' }),
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      }}
                      onClick={isLockedBar ? undefined : () => navigate(`/app/bars/${id}`)}
                    >
                      <Card>
                        <BlockStack gap="300">
                          <InlineStack align="space-between" blockAlign="center">
                            <BlockStack gap="100">
                              <InlineStack gap="200" blockAlign="center">
                                <Text variant="bodyMd" fontWeight="semibold">{name}</Text>
                                {isLockedBar ? (
                                  <Badge tone="default">Inactive - Upgrade to Premium</Badge>
                                ) : (
                                  <Badge tone={is_active ? 'success' : 'default'}>
                                    {is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                )}
                              </InlineStack>
                              <Text variant="bodySm" tone="subdued">
                                Threshold: {thresholdLabel} • Position: {positionLabel}
                              </Text>
                            </BlockStack>

                            {!isLockedBar && (
                              <InlineStack gap="200">
                                {actionLoading === id ? (
                                  <Spinner size="small" />
                                ) : (
                                  <>
                                    <Button
                                      icon={is_active ? HideIcon : ViewIcon}
                                      onClick={(e) => { e.stopPropagation(); handleToggle(id); }}
                                      variant="plain"
                                    />
                                    <Button
                                      icon={EditIcon}
                                      onClick={(e) => { e.stopPropagation(); navigate(`/app/bars/${id}`); }}
                                      variant="plain"
                                    />
                                    <Button
                                      icon={DuplicateIcon}
                                      onClick={(e) => { e.stopPropagation(); handleDuplicate(id); }}
                                      variant="plain"
                                    />
                                    <Button
                                      icon={DeleteIcon}
                                      onClick={(e) => { e.stopPropagation(); confirmDelete(id); }}
                                      variant="plain"
                                      tone="critical"
                                    />
                                  </>
                                )}
                              </InlineStack>
                            )}
                          </InlineStack>

                          {/* Mini preview */}
                          <BarPreview bar={bar} mini currencySymbol={displaySymbol} effectiveThreshold={displayThreshold} />
                        </BlockStack>
                      </Card>
                    </div>
                  );
                })}
              </BlockStack>
            )}
          </Layout.Section>

          {/* Quick Stats & A/B Tests */}
          {bars.length > 0 && (
            <Layout.Section variant="oneThird">
              <BlockStack gap="400">
                <Card>
                  <BlockStack gap="300">
                    <Text variant="headingSm">Quick Stats</Text>
                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Text>Total Bars</Text>
                        <Text fontWeight="semibold">{bars.length}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text>Active Bars</Text>
                        <Text fontWeight="semibold">
                          {bars.filter(b => b.is_active).length}
                        </Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text>Plan</Text>
                        <Badge tone={isPremium ? 'success' : 'default'}>
                          {isPremium ? 'Premium' : 'Basic'}
                        </Badge>
                      </InlineStack>
                    </BlockStack>
                  </BlockStack>
                </Card>

                <Card>
                  <BlockStack gap="300">
                    <Text variant="headingSm">A/B Testing</Text>
                    <Text tone="subdued">
                      Compare bars to find which converts best.
                    </Text>
                    <Button onClick={() => navigate('/app/experiments')}>
                      Manage Experiments
                    </Button>
                  </BlockStack>
                </Card>
              </BlockStack>
            </Layout.Section>
          )}
        </Layout>

        {/* Delete Confirmation Modal */}
        <Modal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title="Delete shipping bar?"
          primaryAction={{
            content: 'Delete',
            destructive: true,
            onAction: handleDelete,
            loading: actionLoading === barToDelete,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setDeleteModalOpen(false),
            },
          ]}
        >
          <Modal.Section>
            <Text>
              This action cannot be undone. The bar will be permanently removed
              from your store.
            </Text>
          </Modal.Section>
        </Modal>
      </Page>
    </AppLayout>
  );
}

export default Dashboard;
